/**
 * Helpers and value catalogues from the prototype's data.jsx, converted
 * to ES modules. People, projects, costs, and actuals all come from real
 * backend APIs.
 */

import { percentToHours, whForMonth } from "@shared/allocation";
import type {
  AllocationCellStored,
  CostRate,
  CostsByPerson,
  ExtrasList,
  Member,
  Person,
  Project,
  RateHistoryEntry,
  YmKey,
} from "@shared/types";

import type { Tone } from "./ui";

/** Job-role catalogue starter set. The user can type new ones. */
export const JOB_ROLES_SEED = [
  "Frontend Developer",
  "Backend Developer",
  "Tester",
  "Analyst",
  "Project Manager",
  "UX",
  "DevOps",
] as const;

export const ROLE_TONES: Record<string, Tone> = {
  "Project Manager": "blue",
  "Frontend Developer": "pink",
  "Backend Developer": "navy",
  Analyst: "y",
  Tester: "g",
  UX: "pink",
  DevOps: "slate",
};

export function roleTone(r: string): Tone {
  return ROLE_TONES[r] ?? "slate";
}

export function getPerson(id: number, people: Person[]): Person | undefined {
  return people.find((p) => p.id === id);
}

export function allocToPct(
  a: AllocationCellStored | null | undefined,
  _person: Pick<Person, "wl"> | undefined,
  _ymKey: YmKey,
): number {
  return a ?? 0;
}

export function allocToHours(
  a: AllocationCellStored | null | undefined,
  person: Pick<Person, "wl"> | undefined,
  ymKey: YmKey,
  extras: ExtrasList = [],
): number {
  if (a == null) return 0;
  return percentToHours(a, person?.wl ?? 100, whForMonth(ymKey, extras));
}

/**
 * API response shape for `GET /api/people/{id}/allocations`. Each entry
 * carries project identity, role, and the bare-percent allocation map.
 */
export interface PersonProjectAllocationRow {
  projectId: number;
  projectName: string;
  projectCode: string;
  status: Project["status"];
  isTimeOff: boolean;
  memberId: number;
  role: string;
  a: Record<YmKey, AllocationCellStored>;
}

/**
 * Aggregate the rollup endpoint's response to a totals map keyed by
 * `YmKey`. The bench / dashboard / grid views consume this shape.
 *
 * Named distinctly from `lib/memberships-api.getPersonAllocations` (the
 * fetch wrapper) so autocomplete doesn't hand callers the wrong one.
 */
export function rollupRowsToTotals(
  rows: PersonProjectAllocationRow[],
  months: Array<{ key: YmKey }>,
): Record<YmKey, number> {
  const r: Record<YmKey, number> = {};
  for (const row of rows) {
    for (const ym of months) {
      const cell = row.a[ym.key];
      if (cell !== undefined) {
        r[ym.key] = (r[ym.key] ?? 0) + cell;
      }
    }
  }
  return r;
}

/**
 * Transitional: roll up a person's allocations from the in-memory `projects`
 * state by walking embedded members. Used by bench / dashboard / grid views
 * until the rollup endpoint is wired through §8 — once that lands the views
 * fetch the rollup directly and pipe through `getPersonAllocations`.
 */
export function aggregatePersonAllocations(
  pid: number,
  projects: Project[],
  months: Array<{ key: YmKey }>,
): Record<YmKey, number> {
  const r: Record<YmKey, number> = {};
  for (const pr of projects) {
    for (const m of pr.members) {
      if (m.pid !== pid) continue;
      for (const ym of months) {
        const cell = m.a[ym.key];
        if (cell !== undefined) {
          r[ym.key] = (r[ym.key] ?? 0) + cell;
        }
      }
    }
  }
  return r;
}

/** Actuals lookup: return logged hours for a person on a project for a month. */
export function getActualHours(
  actuals: Record<string, Record<string, Record<YmKey, number>>>,
  projId: number,
  pid: number | null,
  ymKey: YmKey,
): number {
  if (pid == null) return 0;
  return actuals[String(projId)]?.[String(pid)]?.[ymKey] ?? 0;
}

/**
 * The YYYY-MM key of the previous calendar month relative to `d`. Used by
 * the Import Actuals example-CSV download to fill in a sensible Month cell.
 * Explicit Jan→Dec wrap of prior year (no `getMonth() - 1` arithmetic).
 */
export function previousYmKey(d: Date): YmKey {
  const month = d.getMonth(); // 0-11
  if (month === 0) {
    return `${d.getFullYear() - 1}-12`;
  }
  const mm = String(month).padStart(2, "0");
  return `${d.getFullYear()}-${mm}`;
}

/* CSV helpers */
export function csvEscape(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function toCsv(rows: unknown[][]): string {
  return rows.map((r) => r.map(csvEscape).join(",")).join("\n");
}

export function downloadCsv(filename: string, rows: unknown[][]): void {
  const csv = toCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

export function memberRole(_project: Project | null, member: Member | null): string | null {
  return member?.role ?? null;
}

/** Project rate at a point in time, honoring rateHistory. */
export function getProjectRates(
  proj: Pick<Project, "fr" | "roles" | "rateHistory">,
  ymKey: YmKey,
): { fr: number | null | undefined; roles: Record<string, number> } {
  const hist = proj.rateHistory;
  if (Array.isArray(hist) && hist.length) {
    let match: RateHistoryEntry | null = null;
    for (const e of hist) {
      if (!e.effectiveFrom || e.effectiveFrom <= ymKey) match = e;
      else break;
    }
    if (match) return { fr: match.fr ?? null, roles: match.roles ?? {} };
    const first = hist[0];
    return { fr: first?.fr ?? null, roles: first?.roles ?? {} };
  }
  return { fr: proj.fr, roles: proj.roles };
}

/**
 * Resolve the effective cost rate for (person, project, ymKey) against the
 * flat cost_rates chain. KEEP IN SYNC with apps/api/src/lib/effective-cost.ts.
 * A row covers ymKey when `effective_from ≤ ymKey ≤ valid_to` with each NULL
 * bound treated as ±∞. Per-project chain wins for months it covers, else fall
 * through to the default chain, else null. Callers pass rows ordered by id
 * ASC so the "first match" semantics are deterministic even on a violated
 * overlap invariant.
 */
function pickFromChain(rows: CostRate[], ymKey: YmKey): number | null {
  for (const r of rows) {
    if (r.effectiveFrom !== null && r.effectiveFrom > ymKey) continue;
    if (r.validTo !== null && r.validTo < ymKey) continue;
    return r.rate;
  }
  return null;
}

export function resolveCostRate(
  rows: readonly CostRate[],
  projectId: number | null,
  ymKey: YmKey,
): number {
  const projectChain = projectId !== null ? rows.filter((r) => r.projectId === projectId) : [];
  const projectRate = pickFromChain(projectChain, ymKey);
  if (projectRate !== null) return projectRate;
  const defaultChain = rows.filter((r) => r.projectId === null);
  return pickFromChain(defaultChain, ymKey) ?? 0;
}

export function costRateAt(
  personId: number | string,
  projectId: number | null,
  ymKey: YmKey,
  costs: CostsByPerson,
): number {
  const list = costs[String(personId)] ?? [];
  return resolveCostRate(list, projectId, ymKey);
}

export function rateForMemberAt(proj: Project, member: Member, ymKey: YmKey): number {
  const { fr, roles } = getProjectRates(proj, ymKey);
  if (proj.pm === "flat") return fr ?? 0;
  const role = memberRole(proj, member);
  if (!role) return 0;
  return roles[role] ?? 0;
}

/**
 * Members are returned by the API in insertion order (createdAt ASC) per
 * the members spec. Client-side rendering passes the array through verbatim
 * so editing a row's role never reorders the table. Kept as a function so
 * call sites stay stable if a different surface ever needs to opt back in.
 */
export function sortMembers(members: Member[]): Member[] {
  return members;
}
